import { DateInput } from '@/common/components/DateInput/DateInput'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { FormTransformers } from '@/common/utils/formTransformers'
import { GovernmentAllocationInfoItem } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationInfoItem'
import { CompanyGovernmentAllocationData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { Dispatch, SetStateAction, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { AnticipateEndDateGovernmentAllocationSchema, createAnticipateEndDateGovernmentAllocationSchema } from '../../schemas/anticipateEndDateGovernmentAllocation.schema'
import { useAnticipateEndDateGovernmentAllocation } from '../../hooks/useAnticipateEndDateGovernmentAllocation'

interface AnticipateEndDateGovernmentAllocationModalProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  companyGovernmentAllocation: CompanyGovernmentAllocationData
}

export const AnticipateEndDateGovernmentAllocationModal = ({
  isOpen,
  setIsOpen,
  companyGovernmentAllocation,
}: AnticipateEndDateGovernmentAllocationModalProps) => {
  const { companyId, id: governmentAllocationId } = useParams()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const minDate = new Date()
  const maxDate = new Date(companyGovernmentAllocation?.applicable_end_date)

  const anticipateEndDateGovernmentAllocationSchema = createAnticipateEndDateGovernmentAllocationSchema(
    minDate,
    maxDate
  )

  const {
    control,
    handleSubmit,
    setError,
    reset: resetForm,
    formState: { errors },
  } = useForm<AnticipateEndDateGovernmentAllocationSchema>({
    resolver: zodResolver(anticipateEndDateGovernmentAllocationSchema),
    defaultValues: {
      requested_new_end_date: undefined,
    },
  })

  const onSubmit: SubmitHandler<AnticipateEndDateGovernmentAllocationSchema> = (data) => {
    anticipateEndDateGovernmentAllocation(data)
  }

  const onSuccess = () => {
    resetForm()
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocations', companyId] })
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocation', governmentAllocationId] })
    setIsOpen(false)
  }

  const {
    mutate: anticipateEndDateGovernmentAllocation,
    isPending: anticipateEndDateGovernmentAllocationIsPending,
    isError: anticipateEndDateGovernmentAllocationIsError,
  } = useAnticipateEndDateGovernmentAllocation({
    governmentAllocationId,
    onSuccess,
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    },
  })

  return (
    <>
      <Modal
        isOpen={isOpen && !messages}
        setIsOpen={(value) => {
          if (!value) {
            setTimeout(() => resetForm({ requested_new_end_date: undefined }), 300)
          }

          setIsOpen(value)
        }}
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={anticipateEndDateGovernmentAllocationIsPending}
        disabled={anticipateEndDateGovernmentAllocationIsPending}
        title="Antecipar data final do empenho"
        footer={`${anticipateEndDateGovernmentAllocationIsPending ? 'Antecipando' : 'Antecipar'} data final de empenho`}
        body={
          <div className="flex flex-col gap-y-4 text-white-800">
            <GovernmentAllocationInfoItem
              label="Período de competência"
              value={`${dateTransformer(companyGovernmentAllocation.applicable_start_date)} até ${dateTransformer(
                companyGovernmentAllocation.applicable_end_date,
              )}`}
            />

            <form className="flex flex-col">
              <DateInput
                fieldName="requested_new_end_date"
                control={control}
                errors={errors}
                label="Nova data final do período de competência"
                minDate={minDate}
                maxDate={maxDate}
                markAsRequired
                disabled={anticipateEndDateGovernmentAllocationIsPending}
              />
            </form>
          </div>
        }
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={anticipateEndDateGovernmentAllocationIsError}
        />
      )}
    </>
  )
}
